import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { GuestCountFields, validGuestCounts } from "@/components/GuestCountFields";
import { GuestSummary } from "@/components/GuestSummary";
import { reservationErrorMessage } from "@/lib/reservationErrors";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { format, parse } from "date-fns";
import { de } from "date-fns/locale";
import { Calendar as CalendarIcon, Users, Clock, CheckCircle, Upload, Heart, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = process.env.REACT_APP_LOGO_URL;

const BookingWidget = () => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("");
  const [guestCounts, setGuestCounts] = useState({ adults_count: 0, children_count: 0 });
  const guestCount = guestCounts.adults_count + guestCounts.children_count;
  const [timeSlots, setTimeSlots] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    special_requests: ""
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [confirmedData, setConfirmedData] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchBlockedSlots();
    fetchSettings();
  }, []);

  useEffect(() => {
    if (settings) {
      generateTimeSlots();
    }
  }, [selectedDate, blockedSlots, settings]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
      // Fallback to defaults
      setSettings({
        lunch_enabled: true,
        lunch_opening: "10:30",
        lunch_closing: "14:00",
        dinner_enabled: true,
        dinner_opening: "17:00",
        dinner_closing: "22:30",
        last_booking_offset_minutes: 30
      });
    }
  };

  const fetchBlockedSlots = async () => {
    try {
      const response = await axios.get(`${API}/blocked-slots`);
      setBlockedSlots(response.data);
    } catch (error) {
      console.error("Error fetching blocked slots:", error);
    }
  };

  const generateTimeSlots = () => {
    if (!settings || !settings.opening_hours) return;
    
    const slots = [];
    // Get day of week (monday, tuesday, etc.)
    const dayName = format(selectedDate, 'EEEE').toLowerCase();
    const daySchedule = settings.opening_hours[dayName];
    
    if (!daySchedule) return;
    
    // Helper: Convert time string (HH:MM) to decimal hours
    const timeToDecimal = (timeStr) => {
      const [h, m] = timeStr.split(':').map(Number);
      return h + (m / 60);
    };
    
    // Helper: Calculate last booking time (closing - offset)
    const getLastBookingTime = (closingTime) => {
      const closingDecimal = timeToDecimal(closingTime);
      const offsetHours = (settings.last_booking_offset_minutes || 30) / 60;
      return closingDecimal - offsetHours;
    };
    
    // Build periods based on day schedule
    const periods = [];
    
    // Lunch period
    if (daySchedule.lunch_enabled) {
      const lunchStart = timeToDecimal(daySchedule.lunch_opening);
      const lunchEnd = getLastBookingTime(daySchedule.lunch_closing);
      if (lunchEnd > lunchStart) {
        periods.push({ start: lunchStart, end: lunchEnd });
      }
    }
    
    // Dinner period
    if (daySchedule.dinner_enabled) {
      const dinnerStart = timeToDecimal(daySchedule.dinner_opening);
      const dinnerEnd = getLastBookingTime(daySchedule.dinner_closing);
      if (dinnerEnd > dinnerStart) {
        periods.push({ start: dinnerStart, end: dinnerEnd });
      }
    }

    // Generate 15-minute slots
    periods.forEach(period => {
      for (let hour = period.start; hour <= period.end; hour += 0.25) {
        const hours = Math.floor(hour);
        const minutes = Math.round((hour % 1) * 60);
        const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    });

    setTimeSlots(slots);
  };

  const isSlotBlocked = (time) => {
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    return blockedSlots.some(slot => {
      if (slot.date !== dateStr) return false;
      if (slot.is_full_day) return true;
      if (slot.time_from && slot.time_to) {
        return time >= slot.time_from && time <= slot.time_to;
      }
      return false;
    });
  };

  const isSlotTooSoon = (time) => {
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const now = new Date();
    const slotDateTime = parse(`${dateStr} ${time}`, "yyyy-MM-dd HH:mm", new Date());
    const diffInMinutes = (slotDateTime - now) / (1000 * 60);
    return diffInMinutes < 30;
  };

  const isSlotUnavailable = (time) => {
    return isSlotBlocked(time) || isSlotTooSoon(time);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf', 
                         'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                         'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    
    if (!allowedTypes.includes(file.type)) {
      setUploadError("Nur PNG, JPG, PDF, Word und Excel Dateien erlaubt");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("Datei zu groß. Maximal 5 MB erlaubt");
      return;
    }
    
    setUploadError("");
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result.split(',')[1];
      setUploadedFile({
        filename: file.name,
        data: base64String,
        mimetype: file.type
      });
      toast.success("Datei erfolgreich hochgeladen");
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!validGuestCounts(guestCounts)) {
      toast.error("Bitte wählen Sie mindestens eine erwachsene Person.");
      return;
    }
    if (!selectedDate || !selectedTime) {
      toast.error("Bitte wählen Sie Datum und Uhrzeit");
      return;
    }

    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    // Prevent double submission
    if (isSubmitting) {
      console.log("⚠️ Submission already in progress, ignoring duplicate request");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const reservationData = {
        ...formData,
        reservation_date: format(selectedDate, "yyyy-MM-dd"),
        reservation_time: selectedTime,
        ...guestCounts
      };
      
      if (uploadedFile) {
        reservationData.attachment_filename = uploadedFile.filename;
        reservationData.attachment_data = uploadedFile.data;
        reservationData.attachment_mimetype = uploadedFile.mimetype;
      }

      const response = await axios.post(`${API}/reservations`, reservationData);
      setConfirmed(true);
      setConfirmedData(response.data);
      toast.success(guestCount >= 15 ? "Anfrage erfolgreich gesendet!" : "Reservierung erfolgreich!");
    } catch (error) {
      // Show specific error message from backend
      const errorMessage = reservationErrorMessage(error, "Fehler bei der Reservierung. Bitte versuchen Sie es erneut.");
      toast.error(errorMessage, { duration: 5000 });
      console.error("❌ Error creating reservation:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      special_requests: ""
    });
    setSelectedDate(new Date());
    setSelectedTime("");
    setGuestCounts({ adults_count: 0, children_count: 0 });
    setConfirmed(false);
    setUploadedFile(null);
    setUploadError("");
    setStep(1);
  };

  const nextStep = () => {
    if (step === 1 && !validGuestCounts(guestCounts)) {
      toast.error("Bitte wählen Sie mindestens eine erwachsene Person.");
      return;
    }
    if (step === 1 && (!selectedDate || !selectedTime)) {
      toast.error("Bitte wählen Sie Datum und Uhrzeit");
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  return (
    <div className="min-h-screen p-4" 
         style={{
           background: 'linear-gradient(180deg, #f8f6f3 0%, #ffffff 100%)',
           position: 'relative',
           overflow: 'hidden'
         }}>
      {/* Admin Link - Top Right */}
      <div className="fixed top-3 right-3 md:top-5 md:right-5 z-[100]">
        <a
          data-testid="booking-admin-link"
          href="/admin"
          className="text-xs md:text-sm px-3 py-2 md:px-5 md:py-2.5"
          style={{
            background: 'white',
            color: '#2d5016',
            fontWeight: '600',
            border: '2px solid #2d5016',
            borderRadius: '6px',
            textDecoration: 'none',
            display: 'inline-block',
            transition: 'all 0.3s ease',
            fontFamily: 'Georgia, serif'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#2d5016';
            e.target.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'white';
            e.target.style.color = '#2d5016';
          }}
        >
          🔐 Admin
        </a>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-col items-center justify-center min-h-screen">
        {/* Hero Section */}
        <div className="text-center mb-12 relative">
          {/* Animated Italian Flag */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="italian-flag-stripe" style={{
              width: '80px',
              height: '6px',
              background: '#009246',
              animation: 'slideIn 1s ease-out'
            }}></div>
            <div className="italian-flag-stripe" style={{
              width: '80px',
              height: '6px',
              background: 'white',
              border: '1px solid #ccc',
              animation: 'slideIn 1s ease-out 0.2s',
              animationFillMode: 'backwards'
            }}></div>
            <div className="italian-flag-stripe" style={{
              width: '80px',
              height: '6px',
              background: '#ce2b37',
              animation: 'slideIn 1s ease-out 0.4s',
              animationFillMode: 'backwards'
            }}></div>
          </div>

          {/* Logo with bounce animation */}
          <div className="bounce-animation mb-4 md:mb-6">
            <img src={LOGO_URL} alt="Pizzeria Piazza" className="h-20 md:h-32 w-auto mx-auto" />
          </div>

          {/* Main Title - Responsive */}
          <h1 className="text-3xl md:text-5xl font-bold mb-3 md:mb-4 px-4" style={{
            color: '#2d5016',
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
          }}>
            Benvenuti!
          </h1>
          
          <p className="text-base md:text-xl mb-2 px-4" style={{
            color: '#ce2b37',
            fontFamily: 'Georgia, serif',
            fontWeight: 'bold'
          }}>
            LA CUCINA ITALIANA
          </p>
          
          <p className="text-sm md:text-lg mb-6 md:mb-8 px-4" style={{
            color: '#666',
            fontFamily: 'Georgia, serif'
          }}>
            Reservieren Sie Ihren Tisch bei Pizzeria Piazza Zürich Affoltern
          </p>
        </div>

        {/* Animated Decorative Elements */}
        <div className="floating-element" style={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          fontSize: '60px',
          animation: 'float 3s ease-in-out infinite'
        }}>🍕</div>
        
        <div className="floating-element" style={{
          position: 'absolute',
          bottom: '15%',
          right: '8%',
          fontSize: '70px',
          animation: 'float 4s ease-in-out infinite 1s'
        }}>🍝</div>
        
        <div className="floating-element" style={{
          position: 'absolute',
          top: '30%',
          right: '5%',
          fontSize: '50px',
          animation: 'float 3.5s ease-in-out infinite 0.5s'
        }}>🍷</div>
        
        <div className="floating-element" style={{
          position: 'absolute',
          top: '60%',
          left: '8%',
          fontSize: '55px',
          animation: 'float 4.5s ease-in-out infinite 1.5s'
        }}>🧀</div>
        
        <div className="floating-element" style={{
          position: 'absolute',
          bottom: '30%',
          left: '15%',
          fontSize: '45px',
          animation: 'float 3.8s ease-in-out infinite 0.8s'
        }}>🌿</div>
        
        <div className="floating-element" style={{
          position: 'absolute',
          top: '20%',
          right: '20%',
          fontSize: '40px',
          animation: 'float 4.2s ease-in-out infinite 1.2s'
        }}>🍅</div>

        {/* Call to Action Buttons */}
        <Dialog>
          <DialogTrigger asChild>
            <button
              data-testid="booking-open-button"
              className="reserve-button px-8 md:px-12 py-4 md:py-5"
              style={{
                background: '#ce2b37',
                color: 'white',
                fontSize: '18px',
                fontWeight: '700',
                border: '3px solid #ce2b37',
                borderRadius: '8px',
                cursor: 'pointer',
                boxShadow: '0 6px 20px rgba(206, 43, 55, 0.3)',
                transition: 'all 0.3s ease',
                fontFamily: 'Georgia, serif',
                animation: 'pulse 2s ease-in-out infinite',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-5px) scale(1.05)';
                e.target.style.boxShadow = '0 10px 30px rgba(206, 43, 55, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 6px 20px rgba(206, 43, 55, 0.3)';
              }}
            >
              <span style={{ position: 'relative', zIndex: 1 }}>
                🍴 Jetzt Reservieren 🍴
              </span>
            </button>
          </DialogTrigger>

        <DialogContent data-testid="booking-dialog" aria-describedby={undefined} className="max-w-2xl w-[95vw] md:w-full p-0 overflow-hidden border-2" style={{ 
          maxHeight: '90vh', 
          overflowY: 'auto',
          borderColor: '#2d5016',
          borderRadius: '10px'
        }}>
          <DialogTitle className="sr-only">Tisch reservieren</DialogTitle>
          {!confirmed ? (
            <div style={{ background: 'white' }}>
              {/* Header - Italian Style */}
              <div className="text-center py-6 md:py-8 px-4 md:px-6" style={{
                background: '#f8f6f3',
                borderBottom: '3px solid #ce2b37'
              }}>
                <img src={LOGO_URL} alt="Pizzeria Piazza" className="h-16 md:h-20 w-auto mx-auto mb-3 md:mb-4" />
                <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ 
                  color: '#2d5016',
                  fontFamily: 'Georgia, serif',
                  fontStyle: 'italic'
                }}>
                  Benvenuti!
                </h2>
                <p className="text-xs md:text-sm" style={{ color: '#666', fontFamily: 'Georgia, serif' }}>
                  Ihr Tisch bei Pizzeria Piazza
                </p>
                
                {/* Italian Flag Decoration */}
                <div className="flex justify-center gap-2 mt-4">
                  <div style={{ width: '60px', height: '4px', background: '#009246' }}></div>
                  <div style={{ width: '60px', height: '4px', background: 'white', border: '1px solid #ccc' }}></div>
                  <div style={{ width: '60px', height: '4px', background: '#ce2b37' }}></div>
                </div>
              </div>

              {/* Progress Dots - Italian Colors */}
              <div className="flex justify-center gap-3 py-5 px-6" style={{ background: '#f8f6f3' }}>
                {[1, 2, 3].map((s) => (
                  <div
                    key={s}
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: s === step ? '#2d5016' : '#e0e0e0',
                      transition: 'all 0.3s ease',
                      border: s === step ? '2px solid #ce2b37' : 'none'
                    }}
                  />
                ))}
              </div>

              {/* Content */}
              <div className="p-4 md:p-8" style={{ background: 'white' }}>
                {/* Step 1: Date & Time */}
                {step === 1 && (
                  <div className="space-y-6">
                    <div className="text-center mb-4 md:mb-6">
                      <h3 className="text-xl md:text-2xl font-bold mb-2" style={{ 
                        color: '#2d5016',
                        fontFamily: 'Georgia, serif'
                      }}>
                        Wann dürfen wir Sie erwarten?
                      </h3>
                      <p style={{ color: '#666', fontSize: '13px' }} className="md:text-sm">Wählen Sie Ihren Wunschtermin</p>
                    </div>

                    {/* Date Selection */}
                    <div style={{ 
                      background: '#f8f6f3', 
                      padding: '16px', 
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}
                    className="md:p-6">
                      <Label className="text-sm md:text-base font-semibold mb-3 block flex items-center gap-2" style={{ color: '#2d5016' }}>
                        <CalendarIcon className="w-4 h-4 md:w-5 md:h-5" />
                        Datum
                      </Label>
                      <Button
                        data-testid="booking-date-button"
                        onClick={() => setShowCalendar(!showCalendar)}
                        className="w-full justify-start text-xs md:text-base h-10 md:h-12"
                        style={{
                          background: 'white',
                          color: '#2d5016',
                          border: '2px solid #2d5016'
                        }}
                      >
                        <CalendarIcon className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                        <span className="text-xs md:text-base truncate">{format(selectedDate, "EEEE, dd. MMMM yyyy", { locale: de })}</span>
                      </Button>
                      {showCalendar && (
                        <div className="mt-3 md:mt-4 bg-white p-2 md:p-4 rounded-lg shadow-lg border" style={{ borderColor: '#2d5016' }}>
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(date) => {
                              setSelectedDate(date);
                              setShowCalendar(false);
                            }}
                            locale={de}
                            disabled={(date) => date < new Date()}
                            className="text-sm"
                          />
                        </div>
                      )}
                    </div>

                    {/* Time & Guest Grid - Responsive */}
                    <div className="space-y-4">
                      <div style={{ 
                        background: '#f8f6f3', 
                        padding: '20px', 
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <Label className="text-sm md:text-base font-semibold mb-3 block flex items-center gap-2" style={{ color: '#2d5016' }}>
                          <Clock className="w-4 h-4 md:w-5 md:h-5" />
                          Uhrzeit
                        </Label>
                        <select
                          data-testid="booking-time-select"
                          aria-label="Uhrzeit"
                          value={selectedTime}
                          onChange={(e) => setSelectedTime(e.target.value)}
                          className="w-full p-2 md:p-3 rounded-md text-sm md:text-base"
                          style={{ 
                            border: '2px solid #2d5016',
                            color: '#2d5016',
                            fontFamily: 'Georgia, serif'
                          }}
                          required
                        >
                          <option value="">Wählen...</option>
                          {timeSlots.map((slot) => {
                            const unavailable = isSlotUnavailable(slot);
                            return (
                              <option key={slot} value={slot} disabled={unavailable}>
                                {slot} Uhr {unavailable ? '(Nicht verfügbar)' : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <GuestCountFields value={guestCounts} onChange={setGuestCounts} prefix="booking" />
                    </div>
                    
                    {/* Tooltip for 15+ guests - Displayed separately for better mobile layout */}
                    {guestCount >= 15 && (
                      <div data-testid="booking-large-group-notice" className="mt-4 p-3 md:p-4 rounded-md animate-fadeIn" style={{
                        background: '#fff8dc',
                        border: '2px solid #f0ad4e',
                        fontSize: '12px',
                        color: '#856404'
                      }}>
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 mt-0.5" style={{ color: '#f0ad4e' }} />
                          <div className="flex-1">
                            <p className="font-semibold mb-1 text-xs md:text-sm">ℹ️ Anfrage für große Gruppe</p>
                            <p className="text-[11px] md:text-xs leading-relaxed">
                              Ab 15 Personen wird Ihre Reservierung zunächst als Anfrage behandelt. 
                              Wir melden uns schnellstmöglich bei Ihnen zur Bestätigung.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <Button
                      data-testid="booking-step-one-next"
                      onClick={nextStep}
                      disabled={!selectedTime || !validGuestCounts(guestCounts)}
                      className="w-full h-12 md:h-14 text-base md:text-lg font-semibold rounded-md"
                      style={{
                        background: !selectedTime || !validGuestCounts(guestCounts) ? '#e0e0e0' : '#2d5016',
                        color: 'white',
                        border: 'none',
                        fontFamily: 'Georgia, serif'
                      }}
                    >
                      Weiter →
                    </Button>
                  </div>
                )}

                {/* Step 2: Personal Info */}
                {step === 2 && (
                  <div className="space-y-4 md:space-y-5">
                    <div className="text-center mb-4 md:mb-6">
                      <h3 className="text-xl md:text-2xl font-bold mb-2" style={{ 
                        color: '#2d5016',
                        fontFamily: 'Georgia, serif'
                      }}>
                        Ihre Kontaktdaten
                      </h3>
                      <p style={{ color: '#666', fontSize: '13px' }} className="md:text-sm">Damit wir Sie erreichen können</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold" style={{ color: '#2d5016' }}>Vorname</Label>
                        <Input
                          data-testid="booking-first-name"
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          className="mt-2 h-10 md:h-12 text-sm md:text-base"
                          style={{ border: '2px solid #e0e0e0' }}
                          placeholder="Giovanni"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold" style={{ color: '#2d5016' }}>Nachname</Label>
                        <Input
                          data-testid="booking-last-name"
                          value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          className="mt-2 h-10 md:h-12 text-sm md:text-base"
                          style={{ border: '2px solid #e0e0e0' }}
                          placeholder="Rossi"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-semibold" style={{ color: '#2d5016' }}>E-Mail</Label>
                      <Input
                        data-testid="booking-email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="mt-2 h-10 md:h-12 text-sm md:text-base"
                        style={{ border: '2px solid #e0e0e0' }}
                        placeholder="giovanni@example.com"
                        required
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-semibold" style={{ color: '#2d5016' }}>Telefon</Label>
                      <Input
                        data-testid="booking-phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="mt-2 h-10 md:h-12 text-sm md:text-base"
                        style={{ border: '2px solid #e0e0e0' }}
                        placeholder="+41 79 123 4567"
                        required
                      />
                    </div>

                    <div className="flex gap-2 md:gap-3 pt-3 md:pt-4">
                      <Button
                        data-testid="booking-step-two-back"
                        onClick={prevStep}
                        className="flex-1 h-10 md:h-12 text-sm md:text-base"
                        style={{ 
                          background: 'white',
                          color: '#666',
                          border: '2px solid #e0e0e0'
                        }}
                      >
                        ← Zurück
                      </Button>
                      <Button
                        data-testid="booking-step-two-next"
                        onClick={nextStep}
                        className="flex-1 h-10 md:h-12 text-sm md:text-base"
                        style={{
                          background: '#2d5016',
                          color: 'white',
                          fontFamily: 'Georgia, serif'
                        }}
                      >
                        Weiter →
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Special Requests */}
                {step === 3 && (
                  <div className="space-y-4 md:space-y-5">
                    <div className="text-center mb-4 md:mb-6">
                      <h3 className="text-xl md:text-2xl font-bold mb-2" style={{ 
                        color: '#2d5016',
                        fontFamily: 'Georgia, serif'
                      }}>
                        Besondere Wünsche?
                      </h3>
                      <p style={{ color: '#666', fontSize: '13px' }} className="md:text-sm">Optional - Teilen Sie uns Ihre Wünsche mit</p>
                    </div>

                    <div>
                      <Label className="text-sm font-semibold" style={{ color: '#2d5016' }}>Spezielle Wünsche</Label>
                      <Textarea
                        data-testid="booking-special-requests"
                        value={formData.special_requests}
                        onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                        className="mt-2 text-sm md:text-base"
                        style={{ border: '2px solid #e0e0e0' }}
                        rows={4}
                        placeholder="z.B. Fensterplatz, Allergie-Hinweise, etc."
                      />
                    </div>

                    <div style={{ 
                      background: '#f8f6f3',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <Label className="font-semibold flex items-center gap-2 mb-2" style={{ color: '#2d5016' }}>
                        <Upload className="w-4 h-4" />
                        Datei anhängen (optional)
                      </Label>
                      <p className="text-xs mb-2" style={{ color: '#666' }}>PNG, JPG, PDF, Word, Excel (max. 5 MB)</p>
                      <input
                        data-testid="booking-attachment-input"
                        type="file"
                        onChange={handleFileChange}
                        accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx"
                        className="w-full text-sm"
                      />
                      {uploadedFile && (
                        <p className="text-sm mt-2 flex items-center gap-1" style={{ color: '#2d5016' }}>
                          <CheckCircle className="w-4 h-4" />
                          {uploadedFile.filename}
                        </p>
                      )}
                      {uploadError && (
                        <p className="text-sm text-red-600 mt-2">{uploadError}</p>
                      )}
                    </div>

                    <GuestSummary reservation={{ ...guestCounts, guests_count: guestCount }} prefix="booking-review" />
                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                      <Button
                        data-testid="booking-step-three-back"
                        onClick={prevStep}
                        className="flex-1 h-12"
                        style={{ 
                          background: 'white',
                          color: '#666',
                          border: '2px solid #e0e0e0'
                        }}
                      >
                        ← Zurück
                      </Button>
                      <Button
                        data-testid="booking-submit-button"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !validGuestCounts(guestCounts)}
                        className="flex-1 h-12 text-lg font-semibold relative"
                        style={{
                          background: isSubmitting ? '#999' : '#ce2b37',
                          color: 'white',
                          fontFamily: 'Georgia, serif',
                          opacity: isSubmitting ? 0.7 : 1,
                          cursor: isSubmitting ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {isSubmitting ? (
                          <>
                            <div data-testid="booking-submit-spinner" className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                            Wird gesendet...
                          </>
                        ) : (
                          <>
                            <Heart className="w-5 h-5 mr-2" />
                            Reservieren!
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Confirmation - Italian Style */
            <div className="text-center py-12 px-8" style={{
              background: '#f8f6f3'
            }}>
              <div className="mb-6">
                <CheckCircle className="w-20 h-20 mx-auto" style={{ color: '#2d5016' }} />
              </div>
              <img src={LOGO_URL} alt="Pizzeria Piazza" className="h-16 w-auto mx-auto mb-4" />
              <h2 className="text-3xl font-bold mb-4" style={{ 
                color: '#2d5016',
                fontFamily: 'Georgia, serif',
                fontStyle: 'italic'
              }}>
                {guestCount >= 15 ? "Grazie per la richiesta!" : "Grazie Mille!"}
              </h2>
              <p className="text-lg mb-2" style={{ color: '#666' }}>
                {guestCount >= 15 
                  ? "Wir melden uns schnellstmöglich bei Ihnen"
                  : "Wir freuen uns auf Ihren Besuch"
                }
              </p>
              <p className="text-sm mb-8" style={{ color: '#666', fontStyle: 'italic' }}>
                bei Pizzeria Piazza • LA CUCINA ITALIANA
              </p>
              
              <div style={{ 
                background: 'white',
                padding: '24px',
                borderRadius: '8px',
                marginBottom: '24px',
                border: '2px solid #2d5016',
                textAlign: 'left'
              }}>
                <p className="mb-2"><strong style={{ color: '#2d5016' }}>Datum:</strong> {format(selectedDate, "dd.MM.yyyy", { locale: de })}</p>
                <p className="mb-2"><strong style={{ color: '#2d5016' }}>Uhrzeit:</strong> {selectedTime} Uhr</p>
                <GuestSummary reservation={confirmedData} prefix="booking-confirmed" />
              </div>
              
              <Button
                data-testid="booking-reset-button"
                onClick={resetForm}
                style={{
                  background: 'white',
                  color: '#2d5016',
                  border: '2px solid #2d5016',
                  padding: '12px 32px',
                  fontFamily: 'Georgia, serif'
                }}
              >
                Neue Reservierung
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default BookingWidget;
